import React, { useMemo, useState } from 'react';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { ArrowDown, ArrowUp, Ban, Send } from 'lucide-react';
import { cn } from '@utils/cn';
import { InvoiceStatusBadge } from '@components/atoms/InvoiceStatusBadge';
import { Invoice, InvoiceStatus } from '@models/invoice.model';

type SortDirection = 'asc' | 'desc';

interface SentListProps {
  invoices: Invoice[];
  onOpenInvoice: (invoiceId: string) => void;
  onVoidInvoice: (invoice: Invoice) => void;
  onResendInvoice: (invoice: Invoice) => void;
  className?: string;
}

const formatCurrency = (value: number): string => {
  return `$ ${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const formatDate = (iso?: string): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const sentTime = (invoice: Invoice): number => {
  // Fall back to createdAt so VOIDED-without-sentAt edge cases still sort deterministically.
  const iso = invoice.sentAt || invoice.createdAt;
  return iso ? new Date(iso).getTime() : 0;
};

export const SentList: React.FC<SentListProps> = ({
  invoices,
  onOpenInvoice,
  onVoidInvoice,
  onResendInvoice,
  className = '',
}) => {
  const [search, setSearch] = useState('');
  const [sortDir, setSortDir] = useState<SortDirection>('desc');

  const filteredInvoices = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = term
      ? invoices.filter((invoice) => {
          const number = (invoice.invoiceNumber || '').toLowerCase();
          const email = (invoice.recipientEmail || '').toLowerCase();
          return number.includes(term) || email.includes(term);
        })
      : invoices;
    const sorted = [...filtered].sort((a, b) => sentTime(a) - sentTime(b));
    return sortDir === 'desc' ? sorted.reverse() : sorted;
  }, [invoices, search, sortDir]);

  const toggleSortDir = () => setSortDir((prev) => (prev === 'desc' ? 'asc' : 'desc'));

  if (invoices.length === 0) {
    return (
      <div className={cn('flex-1 flex items-center justify-center px-6 py-10', className)}>
        <p className="text-sm text-gray-500">No invoices sent yet for this project.</p>
      </div>
    );
  }

  return (
    <div className={cn('flex-1 flex flex-col overflow-hidden', className)}>
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
        <div className="flex-1 flex items-center gap-2 px-3 py-1.5 border border-gray-200 rounded bg-white">
          <SearchOutlinedIcon className="w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by invoice # or recipient…"
            className="flex-1 text-sm focus:outline-none bg-transparent"
          />
        </div>
        <button
          type="button"
          onClick={toggleSortDir}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-200 rounded bg-white hover:bg-gray-50"
          title={sortDir === 'desc' ? 'Newest first — click for oldest first' : 'Oldest first — click for newest first'}>
          Date
          {sortDir === 'desc' ? <ArrowDown className="w-3.5 h-3.5" /> : <ArrowUp className="w-3.5 h-3.5" />}
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {filteredInvoices.length === 0 ? (
          <div className="flex items-center justify-center px-6 py-10">
            <p className="text-sm text-gray-500">No invoices match your search.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-xs text-gray-500 uppercase tracking-wide">
                <th className="text-left px-4 py-2.5">Invoice #</th>
                <th className="text-left px-4 py-2.5">Recipient</th>
                <th className="text-left px-4 py-2.5">Sent Date</th>
                <th className="text-left px-4 py-2.5">Items</th>
                <th className="text-right px-4 py-2.5">Total</th>
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="text-right px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredInvoices.map((invoice) => {
                const isSent = invoice.status === InvoiceStatus.SENT;
                return (
                  <tr
                    key={invoice.invoiceId}
                    onClick={() => onOpenInvoice(invoice.invoiceId)}
                    className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer">
                    <td className="px-4 py-3 font-medium text-gray-900">{invoice.invoiceNumber || '—'}</td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-600">{invoice.recipientEmail}</td>
                    <td className="px-4 py-3 text-gray-600">{formatDate(invoice.sentAt)}</td>
                    <td className="px-4 py-3 text-gray-600">{invoice.lineItems?.length ?? 0}</td>
                    <td className="px-4 py-3 text-right font-medium">{formatCurrency(Number(invoice.total))}</td>
                    <td className="px-4 py-3"><InvoiceStatusBadge status={invoice.status} size="sm" /></td>
                    <td className="px-4 py-3 text-right">
                      {isSent && (
                        <div className="inline-flex items-center gap-1.5 justify-end">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onResendInvoice(invoice);
                            }}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-blue-700 hover:bg-blue-50 border border-blue-200"
                            aria-label={`Resend invoice ${invoice.invoiceNumber || ''}`}
                            title="Resend invoice email">
                            <Send className="w-3.5 h-3.5" />
                            Resend
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onVoidInvoice(invoice);
                            }}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-red-700 hover:bg-red-50 border border-red-200"
                            aria-label={`Void invoice ${invoice.invoiceNumber || ''}`}
                            title="Void this invoice">
                            <Ban className="w-3.5 h-3.5" />
                            Void
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
